import * as Yup from "yup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { ForwardRefRenderFunction } from "react";
import { Field, Form, Formik } from "formik";

import BaseInput from "../../../components/elements/BaseInput";
import CheckBox from "../../../components/elements/CheckBox";
import CurrencyFormatInput from "../../../components/elements/CurrencyFormatInput";
import DatePicker from "../../../components/elements/DatePicker";
import FormErrorHandler from "../../../components/elements/FormErrorHandler";
import FormFieldError from "../../../components/elements/FormFieldError";
import Select from "../../../components/elements/Select";
import classes from "./CampaignTemplateBasicDetailsSlide.module.scss";
import {
  calculateEstimatedBudget,
  endDateCheckErrorsOnChange,
  maxBidCheckErrorsOnChange,
  startDateCheckErrorsOnChange,
} from "../../../utils/yupValidationUtils";

const tooltips = {
  showInHubspot:
    "Checking this option will allow a HubSpot user to add and remove a Company to the Campaign. This will be available to All HubSpot users within your account who have permissions to access the Company Record",
  mediaType: (
    <>
      <Typography color="inherit">Please Note</Typography>
      <br />
      Currently, you can only choose display campaigns (image/gif) <em>or</em> video campaigns.
      <br />
      <br />
      <b>If you select video,</b> you will only be able to upload video creatives later in the campaign creation
      process.
    </>
  ),
  maxCompanies:
    "The maximum number of target companies that can be added to this campaign. If left empty or at 0, no limit will be imposed.",
  estimatedBudget: "The Estimated Budget is the Daily Budget multiplied by the number of days the campaign is running.",
};

const mediaTypeOptions = [
  { label: "Display", value: "display" },
  { label: "Video", value: "video" },
];

export type FormInitialValues = {
  templateName: string;
  name: string;
  description: string;
  mediaType: "video" | "display";
  dailyBudget: number | string;
  estimatedBudget: number | string;
  maxBid: number | string;
  startDate: string | Date;
  endDate: string | Date;
  showInHubspot: boolean;
  maxCompanies: number | string;
};

type CampaignBasicDetailsFormProps = {
  formInitialValues: FormInitialValues;
  handleSubmit: (values: FormInitialValues) => void;
  handleInvalidSubmit: () => void;
};

type Ref = HTMLButtonElement;

const CampaignBasicDetailsForm: ForwardRefRenderFunction<Ref, CampaignBasicDetailsFormProps> = (
  { formInitialValues, handleSubmit, handleInvalidSubmit },
  ref,
) => {
  const validationSchema = Yup.object().shape({
    templateName: Yup.string().required("Required field."),
    name: Yup.string().required("Required field."),
    mediaType: Yup.string().required("Required field."),
    dailyBudget: Yup.number().positive("Number must be positive").required("Required field."),
    maxBid: Yup.number()
      .positive("Number must be positive!")
      .max(100, "Max Bid can not exceed $100")
      .when("estimatedBudget", (estimatedBudget: number, schema: any) => {
        return schema.max(estimatedBudget, "Max Bid can not be greater than the Estimated Budget");
      })
      .required("Required field."),
    startDate: Yup.date().min(
      new Date(new Date().setDate(new Date().getDate() - 1)),
      "Please choose a future or current date!",
    ),
    endDate: Yup.date().when("startDate", (startDate: any, schema: any) => {
      if (startDate) {
        const dayAfter = new Date(startDate.getTime() + 86400000);
        return schema.min(dayAfter, "The end date must be after the start date");
      }
      return schema;
    }),
  });

  return (
    <Formik
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnBlur={false}
    >
      {({ values, setFieldValue, setFieldError }) => (
        <Form className={classes.formContainer}>
          <FormErrorHandler action={handleInvalidSubmit} />
          <div className={classes.hubSpotCheckBox}>
            <Field
              as={FormControlLabel}
              name="showInHubspot"
              type="checkbox"
              control={<CheckBox checked={values.showInHubspot} label="Sales Enabled Campaign" />}
            />

            <Tooltip className={classes.tooltip} title={tooltips.showInHubspot}>
              <HelpIcon />
            </Tooltip>
          </div>

          <div className={classes.formContent}>
            <div className={classes.twoColumnInputs}>
              <div className={classes.gridInputs}>
                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.templateName}
                    name="templateName"
                    variant="outlined"
                    label="Template Name *"
                  />

                  <FormFieldError name="templateName" />
                </div>

                <div className={classes.input}>
                  <Field as={BaseInput} value={values.name} name="name" variant="outlined" label="Campaign Name *" />

                  <FormFieldError name="name" />
                </div>

                <div className={classes.textArea}>
                  <Field
                    as={BaseInput}
                    value={values.description}
                    name="description"
                    variant="outlined"
                    label="Description"
                    rows={4}
                    multiline
                  />

                  <FormFieldError name="description" />
                </div>
              </div>

              <div className={classes.gridInputs}>
                <div className={classes.twoColumnInputs}>
                  <div className={classes.inputWithTooltip}>
                    <div className={classes.input}>
                      <Field
                        as={Select}
                        value={values.mediaType}
                        name="mediaType"
                        label="Campaign Media Type"
                        options={mediaTypeOptions}
                      />
                    </div>

                    <Tooltip className={classes.tooltip} title={tooltips.mediaType}>
                      <HelpIcon />
                    </Tooltip>
                  </div>

                  {values.showInHubspot ? (
                    <div className={classes.inputWithTooltip}>
                      <div className={classes.input}>
                        <Field
                          as={BaseInput}
                          value={values.maxCompanies}
                          name="maxCompanies"
                          variant="outlined"
                          label="Max Companies"
                          type="number"
                          InputProps={{
                            inputProps: {
                              min: 0,
                            },
                          }}
                        />

                        <FormFieldError name="maxCompanies" />
                      </div>
                      <Tooltip className={classes.tooltip} title={tooltips.maxCompanies}>
                        <HelpIcon />
                      </Tooltip>
                    </div>
                  ) : null}
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={DatePicker}
                      value={values.startDate}
                      variant="outlined"
                      label="Campaign Start Date *"
                      InputProps={{
                        name: "startDate",
                      }}
                      onChange={(date: Date) => {
                        setFieldValue("startDate", date.toString(), false);
                        const budget = calculateEstimatedBudget(date, values.endDate as Date, +values.dailyBudget);
                        setFieldValue("estimatedBudget", budget, false);
                        startDateCheckErrorsOnChange(date, setFieldError);
                        endDateCheckErrorsOnChange(date, values.endDate as Date, setFieldError);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      }}
                    />

                    <FormFieldError name="startDate" />
                  </div>

                  <div className={classes.input}>
                    <Field
                      as={DatePicker}
                      value={values.endDate}
                      variant="outlined"
                      label="Campaign End Date *"
                      InputProps={{
                        name: "endDate",
                      }}
                      onChange={(date: Date) => {
                        setFieldValue("endDate", (date as Date).toString(), false);
                        const budget = calculateEstimatedBudget(values.startDate as Date, date, +values.dailyBudget);
                        setFieldValue("estimatedBudget", budget, false);
                        endDateCheckErrorsOnChange(values.startDate as Date, date, setFieldError);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      }}
                    />

                    <FormFieldError name="endDate" />
                  </div>
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={CurrencyFormatInput}
                      value={values.dailyBudget}
                      name="dailyBudget"
                      variant="outlined"
                      label="Daily Budget $ *"
                      InputLabelProps={{ shrink: !!values.dailyBudget.toString() }}
                      onChange={({ target }: { target: any }) => {
                        const budget = calculateEstimatedBudget(
                          values.startDate as Date,
                          values.endDate as Date,
                          +target.value,
                        );
                        setFieldValue("estimatedBudget", budget, false);
                        setFieldValue("dailyBudget", target.value, false);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                        if ((target.value as number) > 0 && target.value) {
                          setFieldError("dailyBudget", undefined);
                        }
                      }}
                    />

                    <FormFieldError name="dailyBudget" />
                  </div>

                  <div className={classes.estimatedBudgetWithTooltip}>
                    <div className={classes.input}>
                      <Field
                        as={CurrencyFormatInput}
                        value={values.estimatedBudget}
                        name="estimatedBudget"
                        variant="outlined"
                        label="Estimated Budget $ *"
                        disabled
                        InputLabelProps={{ shrink: !!values.estimatedBudget.toString() }}
                      />
                    </div>

                    <Tooltip className={classes.tooltip} title={tooltips.estimatedBudget}>
                      <HelpIcon />
                    </Tooltip>
                  </div>

                  <div className={`${classes.inputMaxBid} ${classes.input}`}>
                    <Field
                      as={CurrencyFormatInput}
                      value={values.maxBid}
                      name="maxBid"
                      variant="outlined"
                      label="Max Bid $ *"
                      InputLabelProps={{ shrink: !!values.maxBid.toString() }}
                      onChange={({ target }: any) => {
                        setFieldValue("maxBid", target.value, false);
                        maxBidCheckErrorsOnChange(
                          target.value as number,
                          values.estimatedBudget as number,
                          setFieldError,
                        );
                      }}
                    />

                    <FormFieldError name="maxBid" />
                  </div>
                </div>
              </div>
            </div>

            <button ref={ref} className={classes.hiddenBtn} type="submit">
              Submit
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default React.forwardRef(CampaignBasicDetailsForm);
